import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { Commands, Questionnaire } from '@local/client-contracts';
import { Constants } from '@local/common';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LogService } from '@shared/services';
import { SessionService } from '@shared/services/session.service';
import { Logger } from '@unleash-tech/js-logger';
import { cloneDeep, isEqual } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { AnswerSearchService } from 'src/app/bar/services/answers-search.service';
import { QuestionnaireService } from 'src/app/bar/services/questionnaire.service';
import { QuestionnaireData, QuestionnaireEnum } from '../questionnaire.component';
import { questionnaireContent } from '../questionnaire.content';
import { ContextMenuItem, ContextMenuService } from '@shared/components';
import { ContextMenuItemsService } from 'src/app/bar/services/commands/context-menu-items.service';

export type QuestionnaireType = 'question' | 'response';
@UntilDestroy()
@Component({
  selector: 'mapping-questionnaire',
  templateUrl: './mapping-questionnaire.component.html',
  styleUrls: ['./mapping-questionnaire.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MappingQuestionnaireComponent {
  readonly questionnaireContent = questionnaireContent;
  private originalWorksheet: Questionnaire.Worksheet[];
  private logger: Logger;
  private _data: QuestionnaireData;
  isChanged: boolean;
  disabledRunProcess: boolean;
  disabledRunProcessTooltip: string;
  loadingButton: boolean;
  worksheets: Questionnaire.Worksheet[];

  @Input() set data(val: QuestionnaireData) {
    this._data = val;
    this.worksheets = this.data.worksheets || [];
    this.originalWorksheet = cloneDeep(this.worksheets);
    this.disabledRunProcess = this.isDisabledRunProcess;
  }

  get data(): QuestionnaireData {
    return this._data;
  }

  @Output() runProcess = new EventEmitter<{ updatedWorksheets: Questionnaire.Worksheet[]; processId: string }>();
  @Output() goBack = new EventEmitter<QuestionnaireEnum>();

  private get isDisabledRunProcess(): boolean {
    let hasCorrectLine = false;

    for (const worksheet of this.worksheets) {
      for (const questionnaire of worksheet.questionnaire) {
        const questionnaireValid = this.questionnaireValid(questionnaire);
        if (!questionnaireValid?.valid) {
          this.disabledRunProcessTooltip = questionnaireContent.errorTooltip;
          return true;
        }
        if (questionnaireValid?.sameColumn) {
          this.disabledRunProcessTooltip = this.questionnaireContent.errorSameValue;
          return true;
        }
        if (questionnaireValid?.correctLine) {
          hasCorrectLine = true;
        }
      }
    }

    if (!hasCorrectLine) {
      this.disabledRunProcessTooltip = questionnaireContent.errorRequiredOneLineTooltip;
    }

    return !hasCorrectLine;
  }

  private questionnaireValid(questionnaire: Questionnaire.Questionnaire): { valid: boolean; correctLine?: boolean; sameColumn?: boolean } {
    if (!questionnaire) {
      return;
    }

    const questionAddress = questionnaire.questionColumn?.address;
    const responseAddress = questionnaire.responseColumn?.address;

    const rowValid = this.addressValid(questionAddress) && this.addressValid(responseAddress);
    const emptyRow = this.isEmptyColumn(questionAddress) && this.isEmptyColumn(responseAddress);
    const sameColumn = questionAddress && isEqual(questionAddress, responseAddress);

    return { valid: rowValid || emptyRow, sameColumn, correctLine: !!(questionAddress && responseAddress) };
  }

  private addressValid(address: string) {
    return this.questionnaireContent.currentColumnRegex.test(address);
  }

  private isEmptyColumn(address: string) {
    return !address || address === '';
  }

  private get worksheetsChanged(): boolean {
    return !isEqual(this.worksheets, this.originalWorksheet);
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private questionnaireService: QuestionnaireService,
    private answerSearchService: AnswerSearchService,
    private sessionService: SessionService,
    private logService: LogService,
    private contextMenuService: ContextMenuService,
    private contextMenuItemsService: ContextMenuItemsService
  ) {
    this.logger = this.logService.scope('MappingQuestionnaire');
  }

  async onRunProcessClick() {
    if (this.disabledRunProcess) {
      return;
    }
    this.loadingButton = true;
    this.cdr.markForCheck();
    try {
      const processId = await this.questionnaireService.getCurrentProcessId();
      if (processId) {
        this.questionnaireService.openErrorPopup('alreadyProcess');
        this.loadingButton = false;
        this.cdr.markForCheck();
        return;
      }
      const currentSession = await firstValueFrom(this.sessionService.current$);
      const process = await this.questionnaireService.create({
        blobId: this.data.blobId,
        experienceId: this.data.assistantId,
        worksheets: this.questionnaireService.createWorksheetsRequest(this.worksheets),
        context: this.answerSearchService.buildQuestionContext(currentSession, 'Rfp'),
      });
      this.runProcess.emit({ updatedWorksheets: this.worksheets, processId: process.id });
      this.loadingButton = false;
      this.cdr.markForCheck();
    } catch (error) {
      this.logger.error('failed to create rfp process', error);
      const errorMessage = error?.data?.errors?.Worksheets;
      if (errorMessage?.length && errorMessage[0].toLowerCase().includes('duplicate')) {
        this.questionnaireService.openErrorPopup('duplicateMapping');
      } else {
        this.questionnaireService.openErrorPopup('failed');
      }
      this.loadingButton = false;
      this.cdr.markForCheck();
    }
  }

  onChange(worksheet: Questionnaire.Worksheet, index: number) {
    this.worksheets[index] = worksheet;
    this.isChanged = this.worksheetsChanged;
    this.disabledRunProcess = this.isDisabledRunProcess;
    this.cdr.markForCheck();
  }

  openLearnMore() {
    window.open(Constants.LEARN_MORE_RFP_ASSISTANT, '_blank');
  }

  onReset() {
    this.worksheets = cloneDeep(this.originalWorksheet);
    this.disabledRunProcess = this.isDisabledRunProcess;
    this.isChanged = false;
    this.cdr.markForCheck();
  }

  openContextMenu(event: { x: number; y: number }) {
    const items = this.contextMenuItemsService.getContextMenuItems(['uploadNewFile']);
    this.contextMenuService.open(
      event,
      {
        items,
        onInvoke: (item: ContextMenuItem) => {
          this.invoke(item.command);
        },
      },
      { position: 'above' }
    );
  }

  private invoke(command: Commands.Command) {
    if (command.type === 'upload-new-file') {
      this.goBack.emit(QuestionnaireEnum.Upload);
    }
  }
}
